/* eslint-disable max-lines-per-function */
import type { Hook } from '../types';
import type { ExtensionApi } from '../../core/extension';
import type { Option } from '../../helper/standardChart';

const CACHE_KEY = 'standardChartPresetOption';

export function buildStandardChartPresetOption(
  ...args: Parameters<Hook['buildStandardChartPresetOption']>
) {
  const [{ cache, token, hook, util, helper, data }] = args;
  if (cache.has(CACHE_KEY)) {
    return cache.get(CACHE_KEY);
  }

  function formatter(value: number) {
    if (typeof value === 'string') {
      return value;
    }

    const result = hook.formatDataNumberValue(value);
    return result.value + result.unit;
  }

  const uedTokenOption = helper.standardChart.uedTokenToOption(token);
  const ecThemeOption = helper.standardChart.echartsThemeToOption(token);
  const { baseOption } = token.dvStandardChart || {};
  const customTokenOption: Partial<Option> = {
    textStyle: token.textStyle,
    legend: {
      textStyle: token.textStyle
    },
    tooltip: {
      textStyle: token.textStyle,
      dvTitleStyle: token.textStyle,
      valueFormatter: (value: number) => {
        const result = hook.formatDataNumberValue(value);
        return result.value + result.unit;
      },
      formatter: (params: unknown) => {
        const series = Array.isArray(params) ? params : [params];
        // TODO 数据来源一般都在同一下标下，不排除有不同下标的可能
        const dataMetaInfo = hook.parseDataMetaInfo(data[0]);
        const items = [] as Parameters<ExtensionApi['hook']['buildTooltip']>[0]['items'];

        series.forEach(
          (item: {
            value: number;
            seriesType: keyof ExtensionApi['util']['const']['SERIES_2_LEGEND_TYPE_MAP'];
            color: string;
            seriesName: string;
          }) => {
            const result = hook.formatDataNumberValue(item.value, {
              type: 'table',
              unit: dataMetaInfo[item.seriesName]?.unit
            });

            items.push({
              type: util.const.SERIES_2_LEGEND_TYPE_MAP[item.seriesType],
              color: item.color,
              name: item.seriesName,
              value:
                result.value +
                result.unit +
                (dataMetaInfo[item.seriesName]?.unit ? dataMetaInfo[item.seriesName].unit : '')
            });
          }
        );

        return hook.buildTooltip({
          config: hook.buildStandardChartPresetOption(args[0]).tooltip,
          title: series[0]?.axisValueLabel as string,
          items
        });
      }
    },
    xAxis: {
      axisLabel: {
        formatter
      }
    },
    yAxis: {
      axisLabel: {
        formatter
      }
    },
    // * 3D ---------------
    xAxis3D: {
      axisLabel: {
        formatter
      }
    },
    yAxis3D: {
      axisLabel: {
        formatter
      }
    },
    zAxis3D: {
      axisLabel: {
        formatter
      }
    }
  };
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const customOption = helper.standardChart.mergeOption(baseOption, customTokenOption);

  // * 优先级：CustomToken > EChartsTheme > UEDToken
  let option = helper.standardChart.matchMergeOption(
    customOption,
    helper.standardChart.matchMergeOption(ecThemeOption, uedTokenOption)
  );
  option = util.data.deepFilterUndefined(option);

  cache.set(CACHE_KEY, option);
  return option;
}

export default buildStandardChartPresetOption;
